import { dataHora, diaCurto, emData, hora } from "./visual.jsx";

/**
 * A linha da lista montada a partir do caso que o servidor mandou.
 *
 * Existe porque `mesa` é uma PÁGINA (200 de 528) e o caso aberto pode não estar
 * nela: link de caso antigo recebido por mensagem, busca digitada com um caso
 * aberto, pastilha que exclui o caso aberto. Sem esta volta, quatro telas caem
 * juntas e em silêncio — a conversa fica em "Carregando…" para sempre, a ficha
 * devolve painel branco e o botão Petição para de fazer qualquer coisa com
 * `?peticao=1` na URL.
 *
 * Fora do componente porque é conta e não pintura, e porque uma rede de
 * segurança que falha calada precisa de teste (`caso.test.jsx`). As colunas
 * seguem `LinhaDaMesa` (`src/leads.ts`): é o mesmo formato que a lista entrega.
 */
export function linhaDoCaso(caso, etapas, casoId) {
  const r = caso?.registro;
  if (!r || !casoId || r.id !== casoId) return null;
  return {
    id: r.id,
    nome: r.nome,
    telefone: r.telefone ?? null,
    stage: r.stage,
    /* O rótulo da etapa vem do schema, nunca de uma lista escrita aqui: etapa é
       configurável em tempo de execução. Sem o schema ainda carregado sobra a
       chave crua, que é o que o servidor também devolveria. */
    etapa: (etapas ?? []).find((e) => e.chave === r.stage)?.rotulo ?? r.stage,
    tese: r.dados?.tese ?? null,
    responsavel: r.dados?.colaborador ?? null,
    /* O que só a consulta da lista sabe fica em zero: não lidas e foto de perfil
       não mudam nada em quem lê esta linha, e chutar 1 em `tem_avatar` pediria
       uma imagem que talvez não exista. */
    nao_lidas: 0,
    tem_avatar: 0,
    delegado: caso.delegacao ? 1 : 0,
  };
}

/**
 * Quantos dias, em palavras — do jeito que o servidor contou.
 *
 * A tela somava data (`Date.parse` com o relógio da máquina de quem olha) e
 * colapsava qualquer atraso na palavra "vencido": um prazo vencido há quarenta
 * dias lia igual a um vencido ontem. `dias` e `atraso` chegam prontos de
 * `pendencias.listar()`; aqui só se escolhe a frase.
 *
 * Fora do componente pelo mesmo motivo de `linhaDoCaso`: é conta e não pintura,
 * e o sinal negativo é justamente onde o erro passa calado.
 */
export function prazoEmPalavras(p) {
  if (p?.atraso > 0) return `vencido há ${p.atraso} dia(s)`;
  if (p?.dias == null) return "sem data";
  if (p.dias === 0) return "vence hoje";
  return `${p.dias} dia(s)`;
}

/**
 * A pendência merece destaque.
 *
 * O limiar é PINTURA — quanto o olho tem de pular ao varrer a coluna —, e por
 * isso mora na tela; o que é fato do escritório (quantos dias, e se venceu) já
 * chegou decidido do banco.
 */
export const pendenciaAperta = (p) => Boolean(p?.atraso > 0 || (p?.dias != null && p.dias <= 3));

/**
 * O pré-voo da peça: os insumos que `src/minuta.ts` monta no prompt, no estado
 * em que estão neste caso.
 *
 * Pura de propósito — é a regra que a tela desenha e o teste prende. A lista
 * aqui é o espelho do prompt de lá (ficha, fatos marcados com a conversa de
 * apoio, documentos aprovados, reunião): mexer em um pede mexer no outro.
 *
 * `estado`: "ok" é o que já está na mão; "aviso" é o que a peça aceita mas
 * piora (sem tese a busca no acervo vira chute, sem documento conferido a peça
 * sai sem lista de provas); "falta" é o que vira "[A CONFERIR]" no arquivo que
 * a advogada vai revisar.
 */
/** O nome do campo como o escritório o escreveu. Sem esquema, a chave — que é
    feio e visível, e melhor que inventar um rótulo. */
const rotuloDoCampo = (esquema, chave) => (esquema ?? []).find((c) => c.chave === chave)?.rotulo ?? chave;

export function insumosDaPeca({ caso = {}, provas = [], ficha = null, esquema = null } = {}) {
  const marcados = provas.filter((p) => p.na_peca);
  const aprovados = (caso.checklist ?? []).filter((i) => i.estado === "aprovado");
  const reuniao = (caso.gravacoes ?? []).find((r) => r.estado === "aplicada" || r.estado === "transcrita");
  const tese = String(caso.registro?.dados?.tese ?? "").trim();
  const faltam = ficha?.faltam ?? [];

  return [
    {
      chave: "tese",
      estado: tese ? "ok" : "aviso",
      rotulo: "Tese",
      detalhe: tese || "Sem tese na ficha — a busca no acervo fica sem rumo e a peça sai genérica.",
    },
    {
      chave: "fatos",
      estado: marcados.length ? "ok" : "falta",
      rotulo: "Fatos marcados na conversa",
      detalhe: marcados.length
        ? `${marcados.length} de ${provas.length} trecho(s) marcados; a conversa entra como apoio deles.`
        : provas.length
          ? "Nada marcado — marque à direita, senão a peça nasce só da ficha, sem a história do cliente."
          : "Nenhum fato extraído. Sem trecho marcado a conversa fica fora da peça de propósito.",
    },
    {
      chave: "ficha",
      estado: faltam.length ? "falta" : "ok",
      rotulo: "Ficha do cliente",
      /* O RÓTULO do esquema, nunca a chave: a tela imprimia "cpf,
         resumo_atendimento" para quem atende — nome de coluna de banco na cara de
         quem está montando uma petição. `esquema` vem de `/api/mesa`
         (`campos`), o mesmo que a ficha usa para escrever os rótulos. */
      detalhe: faltam.length
        ? `${faltam.length === 1 ? "1 campo em branco vira" : `${faltam.length} campos em branco viram`} "[A CONFERIR]" na peça: ${faltam.map((c) => rotuloDoCampo(esquema, c)).join(", ")}.`
        : "Qualificação completa — nada vira [A CONFERIR] por falta de dado.",
    },
    {
      chave: "documentos",
      estado: aprovados.length ? "ok" : "aviso",
      rotulo: "Documentos aprovados",
      detalhe: aprovados.length
        ? `${aprovados.length} documento(s) conferidos entram como provas.`
        : "Nenhum documento conferido — a peça sai sem lista de provas.",
    },
    {
      chave: "reuniao",
      estado: reuniao ? "ok" : "aviso",
      rotulo: "Reunião de alinhamento",
      detalhe: reuniao
        ? `Reunião ${reuniao.estado}${reuniao.quando ? ` de ${dataHora(reuniao.quando)}` : ""} — a decisão entra na peça.`
        : "Sem reunião transcrita. O que foi decidido em conversa não chega ao modelo.",
    },
  ];
}

/* ==================================================== o caminho do funil */

/**
 * Quanto tempo faz, curto: "agora", "há 3h", "há 9d".
 *
 * Curto porque o lugar é apertado (é um selo de 9px embaixo do ponto da etapa) e
 * porque a pergunta que ele responde é grossa: "isto está parado?". Data exata
 * quem quer é a trilha.
 */
export function haQuantoTempo(quando, agora = new Date()) {
  const d = emData(quando);
  if (!d) return "";
  const min = Math.round((agora.getTime() - d.getTime()) / 60_000);
  if (min < 2) return "agora";
  if (min < 60) return `há ${min}min`;
  if (min < 1440) return `há ${Math.round(min / 60)}h`;
  return `há ${Math.round(min / 1440)}d`;
}

/**
 * O caminho do caso pelo funil, pronto para desenhar.
 *
 * Três decisões moram aqui, e as três são conta e não pintura:
 *
 *   1. **A etapa de PERDA não é caminho.** "Desistência" é a última linha da
 *      tabela por ordem, e desenhá-la no fim do trilho diria que todo caso
 *      caminha para lá. Ela só aparece quando o caso está nela — aí é o fim
 *      dele, e o trilho tem de contar isso.
 *   2. **Etapa que o caso pulou continua "feita".** O manual manda alinhar tese
 *      em algumas teses e não em outras; quem não passou pelo alinhamento não
 *      tem um buraco no meio do caminho, tem uma etapa que não se aplicou.
 *   3. **Desde quando** sai da trilha (`events` com `tipo: "etapa"`), que é a
 *      única fonte que sabe a hora — `records.atualizado_em` muda a cada nota.
 *
 * `caso.test.jsx` prende as três.
 */
export function progressoDoFunil(etapas, stage, trilha = [], agora = new Date()) {
  const perda = (e) => Boolean(e?.final) && !e?.ganho;
  const caminho = (etapas ?? []).filter((e) => !perda(e) || e.chave === stage);
  const indice = caminho.findIndex((e) => e.chave === stage);

  /* A última vez que o caso ENTROU nesta etapa. A trilha vem do mais novo para
     o mais velho, então o primeiro que casar é o que vale. */
  const entrada = (trilha ?? []).find((e) => e?.tipo === "etapa" && e?.para === stage);
  const desde = entrada?.criado_em ?? null;

  return {
    indice,
    total: caminho.length,
    /* O caso morreu no funil: nem "andou", nem "falta". */
    desistiu: indice >= 0 && perda(caminho[indice]),
    desde,
    ha: haQuantoTempo(desde, agora),
    passos: caminho.map((e, n) => ({
      chave: e.chave,
      rotulo: e.rotulo,
      estado: indice < 0 ? "futura" : n < indice ? "feita" : n === indice ? "agora" : "futura",
    })),
  };
}

/* ================================================== os sinais da coluna */

/**
 * O que a coluna do caso tem a DIZER agora, em ordem de urgência.
 *
 * A grade de widgets era fixa: documentos, prazos, ficha, etapa, etiquetas e
 * histórico apareciam sempre, na ordem do código, e quatro deles diziam que não
 * havia nada — "nenhum pedido", "nada em aberto", "Qualificado", "1 etiqueta".
 * Ausência ocupando a área de presença empurra para baixo o que importa, e a
 * ordem do código não é a ordem do dia: o servidor JÁ manda os passos ordenados
 * por urgência (`src/produtividade.ts`) e a coluna ignorava isso abaixo do AGORA.
 *
 * Aqui cada sinal nasce com quatro coisas e nenhuma é enfeite:
 *
 *   - `u`       a urgência, que decide a ordem e quem fica GRANDE (u ≥ 80);
 *   - `tom`     terra trava, âmbar espera fora da casa, verde acabou;
 *   - `relogio` há quanto tempo está assim — o dado que decide insistir,
 *               ligar ou devolver, e que a coluna não tinha em lugar nenhum;
 *   - `apoio`   o que aquele número custa, escrito.
 *
 * Sinal que não tem nada a dizer não vira widget: ele volta em `mudos`, que a
 * coluna resolve numa linha só. E o que o PASSO do servidor já resolve não vira
 * sinal — o cartão do AGORA e um widget dizendo a mesma coisa é a coluna
 * falando duas vezes, que é metade do peso que ela carregava.
 *
 * Pura de propósito: é regra, e `caso.test.jsx` a prende.
 */
export function sinaisDoCaso({ caso = {}, faltam = [], secaoDoPasso = null, agora = new Date() } = {}) {
  const checklist = caso.checklist ?? [];
  const pendencias = caso.pendencias ?? [];
  const gravacoes = caso.gravacoes ?? [];
  const sinais = [];
  const mudos = [];

  const aConferir = checklist.filter((i) => i.estado === "recebido").length;
  const prontos = checklist.filter((i) => i.estado === "aprovado" || i.estado === "dispensado").length;
  if (secaoDoPasso === "documentos") {
    /* O AGORA já é sobre isto, e ele mostra a lista inteira dentro do cartão. */
  } else if (aConferir) {
    sinais.push({
      chave: "documentos",
      u: 90,
      tom: "acao",
      titulo: "Documentos",
      valor: `${aConferir} ${aConferir === 1 ? "chegou" : "chegaram"}`,
      apoio: "ninguém disse ainda se servem",
      verbo: "Conferir",
    });
  } else if (checklist.length && prontos < checklist.length) {
    sinais.push({
      chave: "documentos",
      u: 55,
      tom: "ambar",
      titulo: "Documentos",
      valor: `${checklist.length - prontos} ${checklist.length - prontos === 1 ? "falta" : "faltam"}`,
      apoio: "pedidos ao cliente, sem resposta",
      verbo: "Cobrar",
    });
  } else if (checklist.length) {
    sinais.push({
      chave: "documentos",
      u: 20,
      tom: "verde",
      titulo: "Documentos",
      valor: `${prontos}/${checklist.length}`,
      apoio: "todos resolvidos",
      verbo: "Ver",
    });
  } else mudos.push("documentos");

  /* O prazo mais apertado é a cara do widget: com três pendências, a que decide
     se este caso é urgente é UMA — a que venceu, ou a que vence primeiro. */
  const apertada = [...pendencias].sort((a, b) => (a.dias ?? 999) - (b.dias ?? 999))[0];
  if (pendencias.some((p) => p.atraso > 0)) {
    const vencida = pendencias.filter((p) => p.atraso > 0).sort((a, b) => b.atraso - a.atraso)[0];
    sinais.push({
      chave: "pendencias",
      u: 100,
      tom: "acao",
      titulo: "Prazo vencido",
      valor: `há ${vencida.atraso} dia(s)`,
      apoio: vencida.titulo ?? "prazo em aberto",
      relogio: pendencias.length > 1 ? `+${pendencias.length - 1} em aberto` : null,
      verbo: "Resolver",
    });
  } else if (apertada) {
    sinais.push({
      chave: "pendencias",
      u: pendenciaAperta(apertada) ? 85 : 45,
      tom: pendenciaAperta(apertada) ? "ambar" : "papel",
      titulo: pendencias.length > 1 ? "Prazos" : "Prazo",
      valor: prazoEmPalavras(apertada),
      apoio: apertada.titulo ?? "em aberto",
      relogio: pendencias.length > 1 ? `${pendencias.length} em aberto` : null,
      verbo: "Ver",
    });
  } else mudos.push("prazos");

  if (secaoDoPasso === "ficha") {
    /* idem: o passo já manda preencher, e o cartão do AGORA leva à ficha. */
  } else if (faltam.length) {
    sinais.push({
      chave: "ficha",
      u: 80,
      tom: "acao",
      titulo: "Ficha",
      valor: faltam.length === 1 ? "1 campo" : `${faltam.length} campos`,
      apoio: "que a peça precisa e estão em branco",
      verbo: "Preencher",
    });
  } else {
    /* A ficha completa CONTINUA na coluna, e é a única ausência que fica: ela é
       endereço (`?painel=ficha`, e o roteiro da demonstração aponta para ela) e
       é o que se olha quando não há nada a fazer — "completa para a peça" é
       informação, não a mesma coisa que "nenhum pedido". */
    sinais.push({
      chave: "ficha",
      u: 25,
      tom: "verde",
      titulo: "Ficha",
      valor: "completa",
      apoio: "nada vira [A CONFERIR] por falta de dado",
      verbo: "Ver",
    });
  }

  /*
   * A reunião MARCADA conta antes da gravada: no dia dela a pergunta é "que
   * horas, e onde entro", não o que ficou combinado em maio. Olhar só as
   * gravações fazia a coluna dizer "reuniões: nada em aberto" com uma reunião
   * marcada para amanhã. "Ainda vale" é até duas horas depois do início — a
   * reunião das 9h continua sendo a de agora às 10h30.
   */
  const reunioes = caso.reunioes ?? [];
  const proxima = reunioes
    .filter((r) => r.estado === "marcada")
    .map((r) => ({ r, d: emData(r.quando) }))
    .filter(({ d }) => d && d.getTime() >= agora.getTime() - 2 * 3_600_000)
    .toSorted((a, b) => a.d - b.d)[0];
  /* `gravacoes` vem do servidor da mais nova para a mais velha: a última é a [0]. */
  const ultima = gravacoes[0];
  if (proxima) {
    const hoje = proxima.d.toDateString() === agora.toDateString();
    sinais.push({
      chave: "reunioes",
      u: hoje ? 85 : 40,
      tom: hoje ? "ambar" : "papel",
      titulo: "Reunião",
      valor: `${diaCurto(proxima.r.quando)} ${hora(proxima.r.quando)}`,
      apoio: proxima.r.convite_em ? "marcada, e o cliente tem o link" : "marcada — o cliente ainda não recebeu o link",
      verbo: "Entrar",
    });
  } else if (gravacoes.length)
    sinais.push({
      chave: "reunioes",
      u: 30,
      tom: "papel",
      titulo: "Reuniões",
      valor: String(gravacoes.length),
      apoio: "gravadas e transcritas",
      relogio: ultima?.criado_em ? haQuantoTempo(ultima.criado_em, agora) : null,
      verbo: "Reler",
    });
  else if (secaoDoPasso === "reunioes" || reunioes.some((r) => r.estado === "fila" || r.estado === "marcada")) {
    /* Reunião na fila, ou marcada e já passada sem gravação: não é "nada em
       aberto" — é justamente o que está parado. */
    const passou = reunioes.some((r) => r.estado === "marcada" && r.quando);
    sinais.push({
      chave: "reunioes",
      u: 75,
      tom: "ambar",
      titulo: "Reunião",
      valor: "é a hora",
      apoio: passou
        ? "a data passou — gravar ou remarcar"
        : secaoDoPasso === "reunioes"
          ? "gravar o alinhamento de tese"
          : "na fila, falta marcar a data",
      verbo: secaoDoPasso === "reunioes" ? "Gravar" : "Ver",
    });
  } else mudos.push("reuniões");

  return { sinais: sinais.sort((a, b) => b.u - a.u), mudos };
}

/**
 * De quem é a vez, e há quanto tempo — lido da última mensagem da conversa.
 *
 * É a única informação de tempo que a coluna do caso nunca teve, e é a que
 * decide insistir, ligar ou devolver: um caso "com o cliente há 22h" e um "há 20
 * minutos" pedem coisas diferentes e liam igual. A conta é no navegador porque o
 * dado já está na tela (a conversa desenha a hora de cada bolha do mesmo jeito);
 * o que é FATO do escritório — prazo, atraso, dias na etapa — continua vindo
 * contado do banco.
 */
export function esperaDaConversa(mensagens = [], agora = new Date()) {
  const ultima = [...mensagens].reverse().find((m) => m?.quando);
  if (!ultima) return null;
  const d = emData(ultima.quando);
  if (!d) return null;
  return {
    /* Nossa mensagem por último: a bola está com o cliente. */
    comOCliente: ultima.direcao === "saida",
    ha: haQuantoTempo(ultima.quando, agora),
    horas: Math.floor((agora.getTime() - d.getTime()) / 3_600_000),
  };
}
